import { Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { LazyMarkdownEditor } from "@/book/components/lazy-editor";
import { ReadingCard } from "@/book/components/reading-card";
import { ThankYou } from "@/book/components/thank-you";
import { errorMessage } from "@/shared/api/client";
import {
  useItemTypes,
  useReadingMark,
  useReadings,
  useSubmitReading,
  type Language,
  type ReadingQuery,
  type ReadingSource,
} from "@/shared/api/book";
import { LANGUAGE_LABEL, LANGUAGES, LIST_LABEL, SOURCE_LABEL } from "@/shared/lib/labels";
import { num, useFilters } from "@/shared/lib/use-filters";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { FilterRow, FilterSelect, SearchInput, Select } from "@/shared/ui/filters";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { PageTitle } from "@/shared/ui/page-header";
import { Sheet } from "@/shared/ui/sheet";
import { Empty, ErrorNote, ListSkeleton, Pager } from "@/shared/ui/state";
import { Tabs } from "@/shared/ui/tabs";

/** A `source` üres = a szerzők füle (az alapértelmezett, hogy az URL tiszta maradjon). */
const DEFAULTS = { source: "", q: "", item_type_id: "", language: "", on_list: "", page: "1" };

/** „Még nem jelöltem" = nincs állapota (a kedvenc ettől független) — így az újonnan
 *  felkerültek egy mozdulattal kiszűrhetők. */
const LIST_FILTERS = [
  { value: "none", label: "Még nem jelöltem" },
  { value: "saved", label: LIST_LABEL.saved },
  { value: "done", label: LIST_LABEL.done },
  { value: "skipped", label: LIST_LABEL.skipped },
  { value: "favorite", label: "Kedvenc" },
];

const LANGUAGE_OPTIONS = LANGUAGES.map((l) => ({ value: l, label: LANGUAGE_LABEL[l] }));

export function ReadingsPage() {
  const { values, set } = useFilters(DEFAULTS);
  const { data: types } = useItemTypes();
  const [open, setOpen] = useState(false);
  const source: ReadingSource = values.source === "community" ? "community" : "authors";

  const query: ReadingQuery = {
    source,
    q: values.q,
    item_type_id: num(values.item_type_id),
    language: values.language as Language | "",
    on_list: values.on_list as ReadingQuery["on_list"],
    page: Number(values.page) || 1,
  };
  const { data, isPending, isError, isPlaceholderData } = useReadings(query);
  const mark = useReadingMark();
  const filtered = Object.entries(values).some(
    ([k, v]) => k !== "page" && k !== "source" && Boolean(v),
  );
  // Egyszerre egy beágyazott lejátszó szól: a következő fedlap koppintása bezárja az előzőt.
  const [playing, setPlaying] = useState<number | null>(null);
  const showCommunity = () => set({ source: "community" });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <PageTitle>Ajánló</PageTitle>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          <Plus size={16} />
          Beküldés
        </Button>
      </div>
      {/* A cím önmagában nem mondja meg, mi van itt — a magyarázó sor igen. */}
      <p className="-mt-2 text-[14px] leading-[1.45] text-ink-soft">
        A könyv témájához kapcsolódó ajánlott olvasnivalók, videók és filmek — a szerzőktől és
        az olvasóktól.
      </p>

      {/* A két forrás két fül; a darabszám az aktív szűrést és keresést követi, így látszik,
          ha a másik fülön van találat. */}
      <Tabs
        label="Kitől jön az ajánlás"
        value={source}
        onChange={(next) => set({ source: next === "authors" ? "" : next })}
        tabs={(["authors", "community"] as const).map((s) => ({
          value: s,
          label: SOURCE_LABEL[s],
          count: data?.source_counts[s],
        }))}
      />

      <SearchInput
        label="Keresés az ajánlóban"
        placeholder="Keresés az ajánlóban"
        value={values.q}
        onChange={(q) => set({ q })}
      />

      <FilterRow>
        {types && types.length > 0 && (
          <FilterSelect
            label="Típus"
            allLabel="Minden típus"
            options={types.map((t) => ({ value: t.id, label: t.name }))}
            value={values.item_type_id}
            onChange={(item_type_id) => set({ item_type_id })}
          />
        )}
        <FilterSelect
          label="Nyelv"
          allLabel="Minden nyelv"
          options={LANGUAGE_OPTIONS}
          value={values.language}
          onChange={(language) => set({ language })}
        />
        <FilterSelect
          label="Saját jelöléseim"
          allLabel="Minden jelölés"
          options={LIST_FILTERS}
          value={values.on_list}
          onChange={(on_list) => set({ on_list })}
        />
      </FilterRow>

      {isPending && <ListSkeleton />}
      {isError && <ErrorNote />}
      {data && (
        <>
          <p className="text-[12px] text-muted">{data.total} elem</p>
          {data.items.length === 0 ? (
            filtered ? (
              <Empty>Erre a szűrésre nincs találat.</Empty>
            ) : source === "authors" ? (
              <Empty>A szerzők még nem ajánlottak semmit.</Empty>
            ) : (
              <Empty
                action={
                  <Button variant="outline" onClick={() => setOpen(true)}>
                    Beküldök egy ajánlást
                  </Button>
                }
              >
                Még senki nem küldött be ajánlást. Légy te az első!
              </Empty>
            )
          ) : (
            <div
              className={cn(
                "grid gap-3 transition-opacity md:grid-cols-2",
                isPlaceholderData && "opacity-60",
              )}
            >
              {data.items.map((item) => (
                <ReadingCard
                  key={item.id}
                  item={item}
                  busy={mark.isPending}
                  playing={playing === item.id}
                  onPlay={(on) => setPlaying(on ? item.id : null)}
                  onToggleList={(status) => mark.mutate({ id: item.id, status })}
                  onToggleFavorite={(is_favorite) => mark.mutate({ id: item.id, is_favorite })}
                />
              ))}
            </div>
          )}
          <Pager
            page={data.page}
            total={data.total}
            pageSize={data.page_size}
            onChange={(page) => set({ page: String(page) })}
          />
        </>
      )}

      <Sheet
        open={open}
        onOpenChange={setOpen}
        title="Ajánlás beküldése"
        description="Jóváhagyás után kerül az ajánlóba — addig csak te látod."
      >
        {/* A beküldött ajánlás a közösségi fülön jelenik meg (addig „jóváhagyásra vár"). */}
        <ReadingForm onSubmitted={showCommunity} onDone={() => setOpen(false)} />
      </Sheet>
    </div>
  );
}

/** Olvasói beküldés. A leírás és az ajánlás **Markdownként** megy a szerverre — ugyanaz a
 *  szerkesztő, mint a tapasztalatoknál és az adminban (lustán betöltve). */
function ReadingForm({ onSubmitted, onDone }: { onSubmitted: () => void; onDone: () => void }) {
  const { data: types } = useItemTypes();
  const submit = useSubmitReading();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    title: "",
    body: "",
    url: "",
    item_type_id: "",
    language: "hu" as Language,
    published_year: "",
    recommend_note: "",
    creator: "",
  });

  const update = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate(
      {
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url.trim() || null,
        item_type_id: form.item_type_id ? Number(form.item_type_id) : null,
        language: form.language,
        published_year: form.published_year ? Number(form.published_year) : null,
        recommend_note: form.recommend_note.trim() || null,
        creator: form.creator.trim() || null,
      },
      {
        onSuccess: () => {
          setSent(true);
          onSubmitted();
        },
      },
    );
  };

  if (sent) {
    return (
      <ThankYou title="Köszönjük az ajánlást!" onDone={onDone}>
        Jóváhagyás után bekerül az ajánlóba. Addig „jóváhagyásra vár” jelöléssel csak te látod.
      </ThankYou>
    );
  }
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <Field id="r-title" label="Cím">
        <Input
          id="r-title"
          required
          minLength={2}
          value={form.title}
          onChange={(e) => update({ title: e.target.value })}
        />
      </Field>
      <Field id="r-creator" label="Szerző / alkotó">
        <Input
          id="r-creator"
          placeholder="Pl. a könyv szerzője, a film rendezője"
          value={form.creator}
          onChange={(e) => update({ creator: e.target.value })}
        />
      </Field>
      <Field id="r-url" label="Link">
        <Input
          id="r-url"
          type="url"
          inputMode="url"
          placeholder="https://"
          value={form.url}
          onChange={(e) => update({ url: e.target.value })}
        />
      </Field>
      <Field id="r-type" label="Típus">
        <Select
          id="r-type"
          value={form.item_type_id}
          onChange={(e) => update({ item_type_id: e.target.value })}
        >
          <option value="">Nincs megadva</option>
          {(types ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
      </Field>
      <div className="flex gap-3">
        <Field id="r-lang" label="Nyelv" className="flex-1">
          <Select
            id="r-lang"
            value={form.language}
            onChange={(e) => update({ language: e.target.value as Language })}
          >
            <option value="hu">Magyar</option>
            <option value="en">Angol</option>
          </Select>
        </Field>
        <Field id="r-year" label="Év" className="w-28">
          <Input
            id="r-year"
            inputMode="numeric"
            value={form.published_year}
            onChange={(e) => update({ published_year: e.target.value.replace(/\D/g, "") })}
          />
        </Field>
      </div>
      <LazyMarkdownEditor
        label="Rövid leírás"
        value={form.body}
        onChange={(body) => update({ body })}
        placeholder="Miről szól, kinek való?"
        rows={4}
      />
      <LazyMarkdownEditor
        label="Miért ajánlod, mit adott neked?"
        value={form.recommend_note}
        onChange={(recommend_note) => update({ recommend_note })}
        placeholder="Mit vittél belőle magaddal, kinek ajánlanád?"
        rows={3}
      />
      {submit.isError && <ErrorNote>{errorMessage(submit.error)}</ErrorNote>}
      <Button type="submit" block size="lg" disabled={submit.isPending || form.title.trim().length < 2}>
        {submit.isPending ? "Küldés…" : "Beküldöm"}
      </Button>
    </form>
  );
}

function Field({
  id,
  label,
  children,
  className,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-2 ${className ?? ""}`}>
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}
